/**
 * Bonus Share Calculator
 *
 * Implements tiered bonus share system for share purchases:
 * - Buy 500 shares Receive +20 bonus shares (total: 520 shares)
 * - Buy 1,000 shares Receive +50 bonus shares (total: 1,050 shares)
 * - Buy 2,000 shares Receive +150 bonus shares (total: 2,150 shares)
 * - Buy 3,000 shares Receive +300 bonus shares (total: 3,300 shares)
 * - Buy 5,000 shares Receive +700 bonus shares (total: 5,700 shares)
 * - Buy 10,000 shares Receive +1,500 bonus shares (total: 11,500 shares)
 *
 * IMPORTANT: Bonus shares are excluded from affiliate commission calculations.
 * Commissions are calculated on base shares only (15% USDT + 15% base shares).
 */

export interface BonusTier {
 minShares: number;
 bonusShares: number;
 totalShares: number;
 description: string;
}

export const BONUS_TIERS: BonusTier[] = [
 {
 minShares: 10000,
 bonusShares: 1500,
 totalShares: 11500,
 description: 'Buy 10,000 shares, get +1,500 bonus shares'
 },
 {
 minShares: 5000,
 bonusShares: 700,
 totalShares: 5700,
 description: 'Buy 5,000 shares, get +700 bonus shares'
 },
 {
 minShares: 3000,
 bonusShares: 300,
 totalShares: 3300,
 description: 'Buy 3,000 shares, get +300 bonus shares'
 },
 {
 minShares: 2000,
 bonusShares: 150,
 totalShares: 2150,
 description: 'Buy 2,000 shares, get +150 bonus shares'
 },
 {
 minShares: 1000,
 bonusShares: 50,
 totalShares: 1050,
 description: 'Buy 1,000 shares, get +50 bonus shares'
 },
 {
 minShares: 500,
 bonusShares: 20,
 totalShares: 520,
 description: 'Buy 500 shares, get +20 bonus shares'
 }
];

/**
 * Calculate bonus shares based on the number of shares being purchased
 * @param baseShares - The number of shares being purchased (before bonus)
 * @returns The number of bonus shares to award
 */
export function calculateBonusShares(baseShares: number): number {
 if (!baseShares || baseShares < 0) {
 return 0;
 }

 // Find the highest tier that the purchase qualifies for
 // Tiers are sorted from highest to lowest
 for (const tier of BONUS_TIERS) {
 if (baseShares >= tier.minShares) {
 return tier.bonusShares;
 }
 }

 // No bonus if purchase doesn't meet minimum tier
 return 0;
}

/**
 * Calculate total shares including bonus
 * @param baseShares - The number of shares being purchased (before bonus)
 * @returns Total shares (base + bonus)
 */
export function calculateTotalShares(baseShares: number): number {
 const bonusShares = calculateBonusShares(baseShares);
 return baseShares + bonusShares;
}

/**
 * Get the bonus tier information for a given number of shares
 * @param baseShares - The number of shares being purchased
 * @returns The bonus tier object or null if no tier applies
 */
export function getBonusTier(baseShares: number): BonusTier | null {
 if (!baseShares || baseShares < 0) {
 return null;
 }

 for (const tier of BONUS_TIERS) {
 if (baseShares >= tier.minShares) {
 return tier;
 }
 }

 return null;
}

/**
 * Get the next bonus tier that the user could reach
 * @param baseShares - The current number of shares being purchased
 * @returns The next tier object or null if already at highest tier
 */
export function getNextBonusTier(baseShares: number): BonusTier | null {
 if (!baseShares || baseShares < 0) {
 return BONUS_TIERS[BONUS_TIERS.length - 1]; // Return lowest tier
 }

 // Find current tier
 let currentTierIndex = -1;
 for (let i = 0; i < BONUS_TIERS.length; i++) {
 if (baseShares >= BONUS_TIERS[i].minShares) {
 currentTierIndex = i;
 break;
 }
 }

 // If no current tier, return the lowest tier
 if (currentTierIndex === -1) {
 return BONUS_TIERS[BONUS_TIERS.length - 1];
 }

 // If at highest tier, return null
 if (currentTierIndex === 0) {
 return null;
 }

 // Return the next higher tier
 return BONUS_TIERS[currentTierIndex - 1];
}

/**
 * Calculate shares needed to reach the next bonus tier
 * @param baseShares - The current number of shares being purchased
 * @returns Number of additional shares needed, or 0 if at highest tier
 */
export function getSharesNeededForNextTier(baseShares: number): number {
 const nextTier = getNextBonusTier(baseShares);
 if (!nextTier) {
 return 0; // Already at highest tier
 }

 return Math.max(0, nextTier.minShares - baseShares);
}

/**
 * Format bonus share information for display
 * @param baseShares - The number of shares being purchased
 * @returns Formatted string describing the bonus
 */
export function formatBonusInfo(baseShares: number): string {
 const bonusShares = calculateBonusShares(baseShares);
 
 if (bonusShares === 0) {
 const nextTier = getNextBonusTier(baseShares);
 if (nextTier) {
 const sharesNeeded = getSharesNeededForNextTier(baseShares);
 return `Purchase ${sharesNeeded} more shares to unlock +${nextTier.bonusShares} bonus shares!`;
 }
 return 'No bonus shares for this purchase amount';
 }

 const totalShares = baseShares + bonusShares;
 return ` Bonus: +${bonusShares.toLocaleString()} shares (Total: ${totalShares.toLocaleString()} shares)`;
}

/**
 * Get all bonus tiers for display purposes
 * @returns Array of all bonus tiers
 */
export function getAllBonusTiers(): BonusTier[] {
 return [...BONUS_TIERS];
}

/**
 * Calculate commission-eligible shares (excludes bonus shares)
 * This ensures that affiliate commissions are only calculated on base shares
 * @param totalShares - Total shares including bonus
 * @returns Base shares only (for commission calculation)
 */
export function getCommissionEligibleShares(totalShares: number): number {
 // Work backwards to find base shares
 for (const tier of BONUS_TIERS) {
 if (totalShares === tier.totalShares) {
 return tier.minShares; // Return base shares
 }
 }
 
 // If no exact tier match, assume no bonus was applied
 return totalShares;
}

